import { ArrowLeft, Calendar, Clock, MapPin, User, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import leaveApi from '../LeaveManagement/leaveApi';
import { getAllSites, getAvailableSupervisors, getSiteDetails } from '../SiteManagement/siteApi';
import { Card, PrimaryButton, ProfileImage } from '../ui';
import EntitySelectionDialog from './EntitySelectionDialog';
import HaltPicker from './Halt_Picker';

const CreateRideSchedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rideIdParam = searchParams.get('rideId');
  const isEditMode = !!rideIdParam;
  
  // State for sites and supervisors
  const [sites, setSites] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  
  // State for selected values
  const [selectedFromSite, setSelectedFromSite] = useState(null);
  const [selectedToSite, setSelectedToSite] = useState(null);
  const [selectedFromSupervisor, setSelectedFromSupervisor] = useState(null);
  const [selectedToSupervisor, setSelectedToSupervisor] = useState(null);
  
  // State for date and time inputs
  const [fromDate, setFromDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toDate, setToDate] = useState('');
  const [toTime, setToTime] = useState('');
  const [averageSpeed, setAverageSpeed] = useState('');
  
  // State for halts
  const [haltData, setHaltData] = useState([]);
  
  // State for original ride data (in edit mode)
  const [originalRideData, setOriginalRideData] = useState(null);
  
  // State for dialogs
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [siteDialogLabel, setSiteDialogLabel] = useState('');
  const [showSupervisorDialog, setShowSupervisorDialog] = useState(false);
  const [supervisorDialogLabel, setSupervisorDialogLabel] = useState('');
  const [showHaltPicker, setShowHaltPicker] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Validation error states for date/time
  const [fromDateError, setFromDateError] = useState('');
  const [fromTimeError, setFromTimeError] = useState('');
  const [toDateError, setToDateError] = useState('');
  const [toTimeError, setToTimeError] = useState('');

  // Refs for date pickers
  const fromDatePickerRef = useRef(null);
  const toDatePickerRef = useRef(null);

  // Load sites and supervisors on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load ride data if in edit mode
  useEffect(() => {
    if (isEditMode && rideIdParam && sites.length > 0 && supervisors.length > 0) {
      loadRideDataForEdit();
    }
  }, [isEditMode, rideIdParam, sites.length, supervisors.length]);

  // Restore form data from localStorage if coming back from assign-convoy
  useEffect(() => {
    const storedFormData = localStorage.getItem('createRideFormData');
    if (storedFormData) {
      try {
        const formData = JSON.parse(storedFormData);
        
        // Restore form state
        if (formData.fromDate) setFromDate(formData.fromDate);
        if (formData.fromTime) setFromTime(formData.fromTime);
        if (formData.toDate) setToDate(formData.toDate);
        if (formData.toTime) setToTime(formData.toTime);
        if (formData.averageSpeed) setAverageSpeed(formData.averageSpeed);
        if (formData.haltData) setHaltData(formData.haltData);
        
        // Restore selected sites and supervisors (need to wait for sites/supervisors to load)
        if (formData.selectedFromSiteId) {
          // Will be set after sites load
        }
        if (formData.selectedToSiteId) {
          // Will be set after sites load
        }
        if (formData.selectedFromSupervisorId) {
          // Will be set after supervisors load
        }
        if (formData.selectedToSupervisorId) {
          // Will be set after supervisors load
        }
      } catch (error) {
        console.error('Error restoring form data:', error);
      }
    }
  }, []);

  // Restore selected sites and supervisors after they are loaded
  useEffect(() => {
    if (sites.length > 0) {
      const storedFormData = localStorage.getItem('createRideFormData');
      if (storedFormData) {
        try {
          const formData = JSON.parse(storedFormData);
          if (formData.selectedFromSiteId) {
            const site = sites.find(s => s.site_id === formData.selectedFromSiteId);
            if (site) setSelectedFromSite(site);
          }
          if (formData.selectedToSiteId) {
            const site = sites.find(s => s.site_id === formData.selectedToSiteId);
            if (site) setSelectedToSite(site);
          }
        } catch (error) {
          console.error('Error restoring site data:', error);
        }
      }
    }
  }, [sites]);

  useEffect(() => {
    if (supervisors.length > 0) {
      const storedFormData = localStorage.getItem('createRideFormData');
      if (storedFormData) {
        try {
          const formData = JSON.parse(storedFormData);
          if (formData.selectedFromSupervisorId) {
            const supervisor = supervisors.find(s => s.user_id === formData.selectedFromSupervisorId);
            if (supervisor) setSelectedFromSupervisor(supervisor);
          }
          if (formData.selectedToSupervisorId) {
            const supervisor = supervisors.find(s => s.user_id === formData.selectedToSupervisorId);
            if (supervisor) setSelectedToSupervisor(supervisor);
          }
        } catch (error) {
          console.error('Error restoring supervisor data:', error);
        }
      }
    }
  }, [supervisors]);

  // Validate date and time combinations
  const validateDateTimeSelection = () => {
    // Clear previous errors
    setFromDateError('');
    setFromTimeError('');
    setToDateError('');
    setToTimeError('');

    // Validate "From" date
    if (fromDate) {
      const fromDateObj = parseDate(fromDate);
      if (!fromDateObj) {
        setFromDateError('Please select a valid date');
      } else {
        // Check if From Date is in the past (more than 30 minutes ago if today)
        const now = new Date();
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const fromDateMidnight = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate());

        if (fromDateMidnight.getTime() < todayMidnight.getTime()) {
          setFromDateError('Cannot select a past date');
        }
      }
    }

    // Validate "From" time
    if (fromTime) {
      const fromTimeObj = parseTime(fromTime);
      if (!fromTimeObj) {
        setFromTimeError('Please select a valid time');
      } else if (fromDate) {
        const fromDateObj = parseDate(fromDate);
        if (fromDateObj) {
          const now = new Date();
          const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const fromDateMidnight = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate());

          // If From Date is today, check if time is more than 30 minutes in the past
          if (fromDateMidnight.getTime() === todayMidnight.getTime()) {
            const currentTime = { hour: now.getHours(), minute: now.getMinutes() };
            const fromMinutes = fromTimeObj.hour * 60 + fromTimeObj.minute;
            const currentMinutes = currentTime.hour * 60 + currentTime.minute;
            const thirtyMinutesAgo = currentMinutes - 30;

            if (fromMinutes < thirtyMinutesAgo) {
              setFromTimeError('Cannot select time more than 30 minutes in the past');
            }
          }
        }
      }
    }

    // Validate "To" date
    if (toDate) {
      const toDateObj = parseDate(toDate);
      if (!toDateObj) {
        setToDateError('Please select a valid date');
      } else if (fromDate) {
        const fromDateObj = parseDate(fromDate);
        if (fromDateObj) {
          // Check if "To" date is before "From" date
          if (toDateObj.getTime() < fromDateObj.getTime()) {
            setToDateError('End date must be after start date');
          }
        }
      }
    }

    // Validate "To" time
    if (toTime) {
      const toTimeObj = parseTime(toTime);
      if (!toTimeObj) {
        setToTimeError('Please select a valid time');
      } else if (fromDate && toDate && fromTime) {
        const fromDateObj = parseDate(fromDate);
        const toDateObj = parseDate(toDate);
        const fromTimeObj = parseTime(fromTime);

        if (fromDateObj && toDateObj && fromTimeObj) {
          // If same date, check if times are valid
          if (toDateObj.getTime() === fromDateObj.getTime()) {
            const fromMinutes = fromTimeObj.hour * 60 + fromTimeObj.minute;
            const toMinutes = toTimeObj.hour * 60 + toTimeObj.minute;

            if (toMinutes <= fromMinutes) {
              setToTimeError('Please select a valid time. End time must be after start time');
            }
          } else if (toDateObj.getTime() < fromDateObj.getTime()) {
            // If To date is before From date, time is also invalid
            setToTimeError('Please select a valid date first');
          }
        }
      }
    }
  };

  // Validate when date/time values change
  useEffect(() => {
    validateDateTimeSelection();
  }, [fromDate, fromTime, toDate, toTime]);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [sitesData, supervisorsData] = await Promise.all([
        getAllSites(),
        getAvailableSupervisors()
      ]);
      
      // Sort sites alphabetically
      const sortedSites = [...sitesData].sort((a, b) => 
        (a.site_name || '').localeCompare(b.site_name || '')
      );
      
      setSites(sortedSites);
      setSupervisors(supervisorsData || []);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load sites or supervisors. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load ride data for edit mode
  const loadRideDataForEdit = async () => {
    if (!rideIdParam) return;
    
    setIsLoading(true);
    setError(null);
    try {
      // Fetch all future schedules and find the one with matching ride_id
      const schedules = await driverApi.fetchFutureDriverSchedules();
      const ride = schedules.find(r => r.ride_id === rideIdParam);
      
      if (!ride) {
        setError('Ride not found. Please go back and try again.');
        setIsLoading(false);
        return;
      }

      // Store the original ride data for later use
      setOriginalRideData(ride);

      // Fetch site details
      const fromSiteDetails = await getSiteDetails(ride.from_site_id).catch(() => null);
      const toSiteDetails = await getSiteDetails(ride.to_site_id).catch(() => null);
      
      // Set selected sites
      if (fromSiteDetails) {
        setSelectedFromSite(fromSiteDetails);
      }
      if (toSiteDetails) {
        setSelectedToSite(toSiteDetails);
      }

      // Parse and set dates and times
      if (ride.ride_start_timestamp) {
        const startDate = new Date(ride.ride_start_timestamp);
        const startDateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const startTimeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`; // HH:mm
        setFromDate(startDateStr);
        setFromTime(startTimeStr);
      }

      if (ride.ride_end_timestamp) {
        const endDate = new Date(ride.ride_end_timestamp);
        const endDateStr = endDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`; // HH:mm
        setToDate(endDateStr);
        setToTime(endTimeStr);
      }

      // Set average speed
      if (ride.average_speed) {
        setAverageSpeed(ride.average_speed.toString());
      }

      // Set halts - normalize format to match HaltPicker expected structure
      if (ride.halts && Array.isArray(ride.halts)) {
        const normalizedHalts = ride.halts.map((halt) => {
          if (typeof halt === 'string') {
            // Legacy format - just address
            return {
              address: halt,
              coordinates: null,
            };
          } else if (halt && typeof halt === 'object') {
            // New format - with coordinates
            return {
              address: halt.address || halt.toString(),
              coordinates: halt.coordinates || null,
              location: halt.location || null,
              zoom: halt.zoom || null,
            };
          }
          return {
            address: halt.toString(),
            coordinates: null,
          };
        });
        setHaltData(normalizedHalts);
      } else {
        setHaltData([]);
      }

      // Fetch and set supervisors
      if (ride.from_supervisor_id) {
        try {
          const fromSupervisor = await leaveApi.getUserInfo(ride.from_supervisor_id);
          if (fromSupervisor) {
            setSelectedFromSupervisor({
              user_id: ride.from_supervisor_id,
              user_name: fromSupervisor.user_name || fromSupervisor.name,
              name: fromSupervisor.name,
            });
          }
        } catch (error) {
          console.error('Error fetching from supervisor:', error);
        }
      }

      if (ride.to_supervisor_id) {
        try {
          const toSupervisor = await leaveApi.getUserInfo(ride.to_supervisor_id);
          if (toSupervisor) {
            setSelectedToSupervisor({
              user_id: ride.to_supervisor_id,
              user_name: toSupervisor.user_name || toSupervisor.name,
              name: toSupervisor.name,
            });
          }
        } catch (error) {
          console.error('Error fetching to supervisor:', error);
        }
      }
    } catch (err) {
      console.error('Error loading ride data for edit:', err);
      setError('Failed to load ride data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle site selection
  const handleSiteSelection = (label) => {
    setSiteDialogLabel(label);
    setShowSiteDialog(true);
  };

  const handleSiteConfirm = (siteId) => {
    if (!siteId) return;
    
    const selectedSite = sites.find(s => s.site_id === siteId);
    if (!selectedSite) return;
    
    if (siteDialogLabel === 'From') {
      setSelectedFromSite(selectedSite);
    } else {
      setSelectedToSite(selectedSite);
    }
    
    setShowSiteDialog(false);
  };

  // Handle supervisor selection
  const handleSupervisorSelection = (label) => {
    setSupervisorDialogLabel(label);
    setShowSupervisorDialog(true);
  };

  const handleSupervisorConfirm = (supervisorId) => {
    if (!supervisorId) return;
    
    const selectedSupervisor = supervisors.find(s => s.user_id === supervisorId);
    if (!selectedSupervisor) return;
    
    if (supervisorDialogLabel === 'From') {
      setSelectedFromSupervisor(selectedSupervisor);
    } else {
      setSelectedToSupervisor(selectedSupervisor);
    }
    
    setShowSupervisorDialog(false);
  };

  // Get available sites (filter out already selected site)
  const getAvailableSites = () => {
    return sites.filter(site => {
      if (siteDialogLabel === 'From') {
        return !selectedToSite || site.site_id !== selectedToSite.site_id;
      } else {
        return !selectedFromSite || site.site_id !== selectedFromSite.site_id;
      }
    });
  };

  // Get available supervisors (filter out already selected supervisor)
  const getAvailableSupervisorsList = () => {
    return supervisors.filter(supervisor => {
      if (supervisorDialogLabel === 'From') {
        return !selectedToSupervisor || supervisor.user_id !== selectedToSupervisor.user_id;
      } else {
        return !selectedFromSupervisor || supervisor.user_id !== selectedFromSupervisor.user_id;
      }
    });
  };

  // Convert YYYY-MM-DD to DD-MM-YYYY for display
  const formatDateForInputDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        // YYYY-MM-DD format
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  // Convert DD-MM-YYYY back to YYYY-MM-DD
  const parseDateFromDisplay = (displayString) => {
    if (!displayString) return '';
    try {
      const parts = displayString.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        // DD-MM-YYYY format
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return displayString;
    } catch (e) {
      return displayString;
    }
  };

  // Format date to DD-MM-YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Convert 24-hour time (HH:mm) to 12-hour format (hh:mm a)
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      
      return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeString;
    }
  };

  // Parse date from YYYY-MM-DD (ISO format from date input) or DD-MM-YYYY
  const parseDate = (dateString) => {
    if (!dateString) return null;
    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // Check if it's YYYY-MM-DD format (ISO format from date input)
        if (parts[0].length === 4) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        } else {
          // DD-MM-YYYY format
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
      }
    } catch (e) {
      console.error('Error parsing date:', e);
    }
    return null;
  };

  // Parse time from HH:mm format (24-hour) or hh:mm a format (12-hour)
  const parseTime = (timeString) => {
    if (!timeString) return null;
    try {
      // Check if it's 12-hour format (has space and AM/PM)
      if (timeString.includes(' ')) {
        const timeParts = timeString.split(' ');
        if (timeParts.length === 2) {
          const time = timeParts[0].split(':');
          let hour = parseInt(time[0], 10);
          const minute = parseInt(time[1], 10);
          const period = timeParts[1].toLowerCase();

          if (period === 'pm' && hour !== 12) {
            hour += 12;
          } else if (period === 'am' && hour === 12) {
            hour = 0;
          }

          return { hour, minute };
        }
      } else {
        // 24-hour format (HH:mm)
        const time = timeString.split(':');
        const hour = parseInt(time[0], 10);
        const minute = parseInt(time[1], 10);
        return { hour, minute };
      }
    } catch (e) {
      console.error('Error parsing time:', e);
    }
    return null;
  };

  // Validate date and time constraints
  const validateDateTimeConstraints = () => {
    const fromDateObj = parseDate(fromDate);
    const toDateObj = parseDate(toDate);
    const fromTimeObj = parseTime(fromTime);
    const toTimeObj = parseTime(toTime);

    if (!fromDateObj || !toDateObj || !fromTimeObj || !toTimeObj) {
      alert('Please select valid dates and times');
      return false;
    }

    // Check if "To Date" is before "From Date"
    if (toDateObj < fromDateObj) {
      alert('End date must be after start date');
      return false;
    }

    // If same date, check if times are valid
    if (toDateObj.getTime() === fromDateObj.getTime()) {
      const fromMinutes = fromTimeObj.hour * 60 + fromTimeObj.minute;
      const toMinutes = toTimeObj.hour * 60 + toTimeObj.minute;

      if (toMinutes <= fromMinutes) {
        alert('From and to time cannot be same');
        return false;
      }
    }

    // Check if From Date is today and time is more than 30 minutes in the past
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fromDateMidnight = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate());

    if (fromDateMidnight.getTime() === todayMidnight.getTime()) {
      const currentTime = { hour: now.getHours(), minute: now.getMinutes() };
      const fromMinutes = fromTimeObj.hour * 60 + fromTimeObj.minute;
      const currentMinutes = currentTime.hour * 60 + currentTime.minute;
      const thirtyMinutesAgo = currentMinutes - 30;

      if (fromMinutes < thirtyMinutesAgo) {
        alert('Cannot select time more than 30 minutes in the past');
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = () => {
    // Check for validation errors first
    if (fromDateError || fromTimeError || toDateError || toTimeError) {
      alert('Please fix the date and time errors before submitting');
      return;
    }

    // Validate required fields
    if (!selectedFromSite || !selectedToSite || !fromDate || !fromTime || 
        !toDate || !toTime || !averageSpeed || !selectedFromSupervisor || !selectedToSupervisor) {
      alert('Please fill all the above details to continue');
      return;
    }

    // Validate date and time constraints
    if (!validateDateTimeConstraints()) {
      return;
    }

    // Prepare ride data
    const rideData = {
      from: selectedFromSite,
      from_date: formatDateForDisplay(fromDate),
      from_time: formatTimeTo12Hour(fromTime),
      to: selectedToSite,
      to_date: formatDateForDisplay(toDate),
      to_time: formatTimeTo12Hour(toTime),
      average_speed: averageSpeed,
      from_supervisor: selectedFromSupervisor,
      to_supervisor: selectedToSupervisor,
      halts: haltData,
    };

    // In edit mode, include original ride data for reference
    if (isEditMode && originalRideData) {
      rideData.originalRide = originalRideData;
      rideData.ride_id = rideIdParam;
    }

    console.log('Ride Schedule Data:', rideData);
    
    // Store ride data in localStorage as backup (for assign-convoy)
    localStorage.setItem('pendingRideData', JSON.stringify(rideData));
    
    // Store form state in localStorage (for restoring form when coming back)
    const formData = {
      fromDate,
      fromTime,
      toDate,
      toTime,
      averageSpeed,
      haltData,
      selectedFromSiteId: selectedFromSite?.site_id,
      selectedToSiteId: selectedToSite?.site_id,
      selectedFromSupervisorId: selectedFromSupervisor?.user_id,
      selectedToSupervisorId: selectedToSupervisor?.user_id,
    };
    localStorage.setItem('createRideFormData', JSON.stringify(formData));
    
    // Navigate to convoy assignment page
    const encodedRideData = encodeURIComponent(JSON.stringify(rideData));
    if (isEditMode) {
      setSearchParams({ view: 'assign-convoy', rideData: encodedRideData, rideId: rideIdParam });
    } else {
      setSearchParams({ view: 'assign-convoy', rideData: encodedRideData });
    }
  };

  // Handle adding halt
  const handleAddHalt = () => {
    setShowHaltPicker(true);
  };

  // Handle halt picker confirmation
  const handleHaltConfirm = (haltResult) => {
    if (haltResult && haltResult.address) {
      setHaltData(prev => [...prev, {
        address: haltResult.address,
        coordinates: haltResult.coordinates,
        location: haltResult.location,
        zoom: haltResult.zoom,
      }]);
    }
    setShowHaltPicker(false);
  };

  // Handle removing halt
  const handleRemoveHalt = (index) => {
    setHaltData(prev => prev.filter((_, i) => i !== index));
  };

  // Get minimum date for date inputs (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum time for time inputs
  const getMinTime = (isFromTime) => {
    if (isFromTime) {
      // For "From" time, check if date is today
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDate = parseDate(fromDate);
      
      if (selectedDate && selectedDate.getTime() === today.getTime()) {
        const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
        return `${String(thirtyMinutesAgo.getHours()).padStart(2, '0')}:${String(thirtyMinutesAgo.getMinutes()).padStart(2, '0')}`;
      }
      return '00:00';
    } else {
      // For "To" time, check if same date as "From" date
      const fromDateObj = parseDate(fromDate);
      const toDateObj = parseDate(toDate);
      
      if (fromDateObj && toDateObj && fromDateObj.getTime() === toDateObj.getTime() && fromTime) {
        // Same date - "To" time must be after "From" time
        const fromTimeObj = parseTime(fromTime);
        if (fromTimeObj) {
          // Add 1 minute to from time as minimum
          const minMinutes = fromTimeObj.hour * 60 + fromTimeObj.minute + 1;
          const minHour = Math.floor(minMinutes / 60) % 24;
          const minMinute = minMinutes % 60;
          return `${String(minHour).padStart(2, '0')}:${String(minMinute).padStart(2, '0')}`;
        }
      }
      return '00:00';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setSearchParams({})}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Back"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div>
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            {isEditMode ? 'Edit Ride Schedule' : 'Create Ride Schedule'}
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            {isEditMode 
              ? 'Update the details for this ride schedule'
              : 'Fill in the details to create a new ride schedule'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto mb-6">
        <div className="space-y-6">
          {/* Ride Details Section */}
          <div>
            <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1rem' }}>
              Ride Details
            </h3>
            
            {/* From Site Card */}
            <Card.Gradient className="mb-4 p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                From
              </h5>
              
              {/* Site Selection */}
              <div className="mb-4">
                <button
                  onClick={() => handleSiteSelection('From')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <MapPin className={`w-5 h-5 ${selectedFromSite ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`flex-1 text-left ${selectedFromSite ? 'text-black' : 'text-gray-500'}`}>
                    {selectedFromSite ? selectedFromSite.site_name : 'Select Site'}
                  </span>
                </button>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                    <input
                      type="date"
                      ref={fromDatePickerRef}
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      min={getMinDate()}
                      className="absolute opacity-0 w-full h-12 cursor-pointer z-20"
                      style={{ left: 0, top: 0 }}
                    />
                    <input
                      type="text"
                      value={formatDateForInputDisplay(fromDate)}
                      placeholder="DD-MM-YYYY"
                      readOnly
                      onClick={() => fromDatePickerRef.current?.showPicker?.() || fromDatePickerRef.current?.click()}
                      className={`w-full h-12 pl-10 pr-12 border rounded-lg bg-white focus:outline-none focus:ring-2 cursor-pointer ${
                        fromDateError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => fromDatePickerRef.current?.showPicker?.() || fromDatePickerRef.current?.click()}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors z-30"
                      title="Open date picker"
                    >
                      <Calendar className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  {fromDateError && (
                    <p className="text-red-600 text-xs mt-1">{fromDateError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="time"
                      value={fromTime}
                      onChange={(e) => setFromTime(e.target.value)}
                      min={getMinTime(true)}
                      className={`w-full h-12 pl-10 pr-3 border rounded-lg bg-white focus:outline-none focus:ring-2 ${
                        fromTimeError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                  </div>
                  {fromTimeError && (
                    <p className="text-red-600 text-xs mt-1">{fromTimeError}</p>
                  )}
                </div>
              </div>
            </Card.Gradient>

            {/* To Site Card */}
            <Card.Gradient className="mb-4 p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                To
              </h5>
              
              {/* Site Selection */}
              <div className="mb-4">
                <button
                  onClick={() => handleSiteSelection('To')}
                  className="w-full h-12 border border-gray-300 rounded-lg px-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <MapPin className={`w-5 h-5 ${selectedToSite ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`flex-1 text-left ${selectedToSite ? 'text-black' : 'text-gray-500'}`}>
                    {selectedToSite ? selectedToSite.site_name : 'Select Site'}
                  </span>
                </button>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                    <input
                      type="date"
                      ref={toDatePickerRef}
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      min={fromDate || getMinDate()}
                      className="absolute opacity-0 w-full h-12 cursor-pointer z-20"
                      style={{ left: 0, top: 0 }}
                    />
                    <input
                      type="text"
                      value={formatDateForInputDisplay(toDate)}
                      placeholder="DD-MM-YYYY"
                      readOnly
                      onClick={() => toDatePickerRef.current?.showPicker?.() || toDatePickerRef.current?.click()}
                      className={`w-full h-12 pl-10 pr-12 border rounded-lg bg-white focus:outline-none focus:ring-2 cursor-pointer ${
                        toDateError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => toDatePickerRef.current?.showPicker?.() || toDatePickerRef.current?.click()}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors z-30"
                      title="Open date picker"
                    >
                      <Calendar className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  {toDateError && (
                    <p className="text-red-600 text-xs mt-1">{toDateError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="time"
                      value={toTime}
                      onChange={(e) => setToTime(e.target.value)}
                      min={fromDate === toDate ? fromTime : getMinTime(false)}
                      className={`w-full h-12 pl-10 pr-3 border rounded-lg bg-white focus:outline-none focus:ring-2 ${
                        toTimeError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                  </div>
                  {toTimeError && (
                    <p className="text-red-600 text-xs mt-1">{toTimeError}</p>
                  )}
                </div>
              </div>
            </Card.Gradient>

            {/* Average Speed Card */}
            <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Average Speed
              </h5>
              <input
                type="number"
                value={averageSpeed}
                onChange={(e) => setAverageSpeed(e.target.value)}
                placeholder="Enter speed in km/h"
                className="w-full h-12 px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="text-sm text-gray-500 mt-2">km/h</p>
            </Card.Gradient>
          </div>

          {/* Assign Employee Section */}
          <div className="mt-8">
            <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1rem' }}>
              Assign Employee
            </h3>
            
            {/* From Supervisor Card */}
            <Card.Gradient className="mb-4 p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                From Employee
              </h5>
              <button
                onClick={() => handleSupervisorSelection('From')}
                className="w-full border border-gray-300 rounded-lg px-3 py-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                {selectedFromSupervisor ? (
                  <ProfileImage
                    userId={selectedFromSupervisor.user_id}
                    userName={selectedFromSupervisor.user_name || selectedFromSupervisor.name}
                    className="w-10 h-10"
                    showLoading={false}
                  />
                ) : (
                  <User className="w-5 h-5 text-gray-400" />
                )}
                <span className={`flex-1 text-left ${selectedFromSupervisor ? 'text-black' : 'text-gray-500'}`}>
                  {selectedFromSupervisor ? (selectedFromSupervisor.user_name || selectedFromSupervisor.name) : 'Select Employee'}
                </span>
              </button>
            </Card.Gradient>

            {/* To Supervisor Card */}
            <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                To Employee
              </h5>
              <button
                onClick={() => handleSupervisorSelection('To')}
                className="w-full border border-gray-300 rounded-lg px-3 py-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                {selectedToSupervisor ? (
                  <ProfileImage
                    userId={selectedToSupervisor.user_id}
                    userName={selectedToSupervisor.user_name || selectedToSupervisor.name}
                    className="w-10 h-10"
                    showLoading={false}
                  />
                ) : (
                  <User className="w-5 h-5 text-gray-400" />
                )}
                <span className={`flex-1 text-left ${selectedToSupervisor ? 'text-black' : 'text-gray-500'}`}>
                  {selectedToSupervisor ? (selectedToSupervisor.user_name || selectedToSupervisor.name) : 'Select Employee'}
                </span>
              </button>
            </Card.Gradient>
          </div>

          {/* Halts Section */}
          <div className="mt-8">
            <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1rem' }}>
              Halts
            </h3>
              
              {/* Display existing halts */}
              {haltData.map((halt, index) => (
                <Card.Gradient key={index} className="mb-4 p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h5 className={typography.h5} style={{ color: '#000000' }}>
                      Halt • {index + 1}
                    </h5>
                    <button
                      onClick={() => handleRemoveHalt(index)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-gray-800">{halt.address || 'Unknown Location'}</p>
                      {halt.coordinates && (
                        <p className="text-sm text-gray-500 mt-1">
                          📍 {halt.coordinates.latitude.toFixed(6)}, {halt.coordinates.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                </Card.Gradient>
              ))}

              {/* Add Halt Button */}
              <button
                onClick={handleAddHalt}
                className="w-full h-12 border border-gray-300 rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <span className="text-green-600 text-xl">+</span>
              </button>
            </div>
        </div>
      </div>
      {/* Submit Button */}
        <PrimaryButton
          onClick={handleSubmit}
          className="w-full"
        >
          {isEditMode ? 'Proceed to Edit Convoy Assign' : 'Proceed to Convoy Assignment'}
        </PrimaryButton>
      


      {/* Site Selection Dialog - Rendered using Portal to display on top */}
      {showSiteDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showSiteDialog}
          onClose={() => setShowSiteDialog(false)}
          onConfirm={handleSiteConfirm}
          entityType="site"
          dialogTitle={`Select ${siteDialogLabel} Site`}
          availableEntities={getAvailableSites()}
        />,
        document.body
      )}

      {/* Supervisor Selection Dialog - Rendered using Portal to display on top */}
      {showSupervisorDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showSupervisorDialog}
          onClose={() => setShowSupervisorDialog(false)}
          onConfirm={handleSupervisorConfirm}
          entityType="employee"
          dialogTitle={`Select ${supervisorDialogLabel} Employee`}
          availableEntities={getAvailableSupervisorsList()}
        />,
        document.body
      )}

      {/* Halt Picker Dialog - Rendered using Portal to display on top */}
      <HaltPicker
        isOpen={showHaltPicker}
        onClose={() => setShowHaltPicker(false)}
        onConfirm={handleHaltConfirm}
      />

    </div>
  );
};

export default CreateRideSchedule;

